import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../services/language.service';
import { NAVBAR_MENU } from '../../constants/navbar-menu.constants';
import { LanguageCode, SUPPORTED_LANGUAGES } from '../../constants/language.contants';
import { Language } from '../../models/language.model';
import { LocalBusinessSchemaService } from '../../services/local-business-schema.service';
import { FooterSeoLinks, FooterSeoLinksService } from '../../services/footer-seo-links.service';
import type { FooterSeoLink } from '../../utils/footer-seo-links.util';
import { quoteHubPath } from '../../utils/quote-route-path.util';
import { SiteConfigService } from '../../services/site-config.service';
import { normalizePublicPagePath } from '../../utils/public-url-path.util';

@Component({
  selector: 'app-footer-placeholder',
  imports: [CommonModule],
  templateUrl: './footer-placeholder.component.html',
  styleUrl: './footer-placeholder.component.scss'
})
export class FooterPlaceholderComponent implements OnInit, OnDestroy {
  navbarMenu = NAVBAR_MENU;
  /**
   * A signal, not plain fields: the links can arrive while Angular is rendering (the service is fed
   * by a signal-backed observable). A signal change re-renders the footer in the same pass, where a
   * plain field threw NG0100 (ExpressionChangedAfterItHasBeenChecked) in development.
   */
  private readonly seoLinks = signal<FooterSeoLinks>({ airportLinks: [], destinationLinks: [] });
  private languageService?: LanguageService;

  currentLanguage: Language = { ...SUPPORTED_LANGUAGES[0]! };
  private seoLinksSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private localBusinessSchema: LocalBusinessSchemaService,
    private footerSeoLinksService: FooterSeoLinksService,
    readonly siteConfig: SiteConfigService,
  ) {
    if (typeof window !== 'undefined') {
      this.languageService = inject(LanguageService);
    }
  }

  ngOnInit(): void {
    const language = this.resolveLanguageFromRoute();
    this.currentLanguage = { ...language };
    this.localBusinessSchema.apply();
    this.loadSeoLinks();
  }

  ngOnDestroy(): void {
    this.seoLinksSub?.unsubscribe();
  }

  get airportSeoLinks(): FooterSeoLink[] {
    return this.seoLinks().airportLinks;
  }

  get destinationSeoLinks(): FooterSeoLink[] {
    return this.seoLinks().destinationLinks;
  }

  get hasFooterSeoLinks(): boolean {
    return this.airportSeoLinks.length > 0 || this.destinationSeoLinks.length > 0;
  }

  onLanguageSelect(langCode: LanguageCode): void {
    this.languageService?.setLanguage(langCode, true);
  }

  getTranslation(key: string): string {
    return this.translations[key]?.[this.currentLanguage.code] || key;
  }

  translations: any = {
    home: {
      en: 'Homepage',
      de: 'Startseite',
      ru: 'Главная страница',
      tr: 'Anasayfa',
    },
    contactus: {
      en: 'Contact Us',
      de: 'Kontaktiere uns',
      tr: 'Bize Ulaşın',
      ru: 'Свяжитесь с нами',
    },
    quicklinks: {
      en: 'Quick Links',
      de: 'Schnelle Links',
      tr: 'Hızlı Bağlantılar',
      ru: 'Быстрые ссылки',
    },
    followus: {
      en: 'Follow Us',
      de: 'Folge uns',
      tr: 'Bizi takip et',
      ru: 'Следите за нами',
    },
    aboutus: {
      en: 'About Us',
      de: 'Über uns',
      tr: 'Hakkımızda',
      ru: 'О нас',
    },
    ourservices: {
      en: 'Our Services',
      de: 'Unsere Leistungen',
      tr: 'Hizmetlerimiz',
      ru: 'Наши услуги',
    },
    blog: {
      en: 'Blog',
      de: 'Blog',
      tr: 'Blog',
      ru: 'Блог',
    },
    newBlog: {
      en: 'New Blog',
      de: 'Neuer Blog',
      tr: 'Yeni Blog',
      ru: 'Новый блог',
    },
    contact: {
      en: 'Contact',
      de: 'Kontakt',
      tr: 'İletişim',
      ru: 'Контакт',
    },
    gallery: {
      en: 'Gallery',
      de: 'Galerie',
      tr: 'Galeri',
      ru: 'Галерея',
    },
    faq: {
      en: 'FAQ',
      de: 'FAQ',
      tr: 'SSS',
      ru: 'FAQ',
    },
    paymentOptions: {
      en: 'Payment Options',
      de: 'Zahlungsoptionen',
      tr: 'Ödeme Seçenekleri',
      ru: 'Способы оплаты',
    },
    airportPrices: {
      en: 'Airport Transfer Prices',
      de: 'Flughafen-Transferpreise',
      tr: 'Havalimanı Transfer Fiyatları',
      ru: 'Цены на трансфер из аэропорта',
    },
    quotePrices: {
      en: 'Transfer Price Information',
      de: 'Transferpreis-Informationen',
      tr: 'Transfer Fiyat Bilgileri',
      ru: 'Информация о ценах на трансфер',
    },
    privacyPolicy: {
      en: 'Privacy Policy',
      de: 'Datenschutz-Bestimmungen',
      tr: 'Gizlilik Politikası',
      ru: 'Политика конфиденциальности',
    },
    termsOfService: {
      en: 'Terms of Service',
      de: 'Nutzungsbedingungen',
      tr: 'Hizmet Şartları',
      ru: 'Условия обслуживания',
    },
    allRightsReserved: {
      en: 'All Rights Reserved.',
      de: 'Alle Rechte vorbehalten.',
      tr: 'Tüm Hakları Saklıdır.',
      ru: 'Все права защищены.',
    },
    allLanguages: {
      en: 'Languages',
      de: 'Sprachen',
      tr: 'Diller',
      ru: 'Языки',
    },
    pricesByAirport: {
      en: 'Prices by Airport',
      de: 'Preise nach Flughafen',
      tr: 'Havalimanına Göre Fiyatlar',
      ru: 'Цены по аэропортам',
    },
    popularDestinations: {
      en: 'Popular Destinations',
      de: 'Beliebte Ziele',
      tr: 'Popüler Destinasyonlar',
      ru: 'Популярные направления',
    }
  };

  private resolveLanguageFromRoute(): Language {
    let currentRoute: ActivatedRoute | null = this.route;
    while (currentRoute) {
      const language = currentRoute.snapshot.data['language'] as string | undefined;
      if (language) {
        const match = SUPPORTED_LANGUAGES.find(({ code }) => code === language);
        if (match) {
          return { ...match };
        }
      }
      currentRoute = currentRoute.parent;
    }
    return { ...SUPPORTED_LANGUAGES[0]! };
  }

  quoteHubPath(langCode: LanguageCode = this.currentLanguage.code): string {
    return quoteHubPath(langCode);
  }

  homePath(langCode: LanguageCode = this.currentLanguage.code): string {
    return normalizePublicPagePath(`/${langCode}`);
  }

  menuPath(menuKey: keyof typeof NAVBAR_MENU, langCode: LanguageCode = this.currentLanguage.code): string {
    const slug = NAVBAR_MENU[menuKey]?.slug?.[langCode] || NAVBAR_MENU[menuKey]?.slug?.en || '';
    return normalizePublicPagePath(`/${langCode}/${slug}`);
  }

  trackSeoLink(_index: number, link: FooterSeoLink): string {
    return link.path;
  }

  private loadSeoLinks(): void {
    this.seoLinksSub?.unsubscribe();
    this.seoLinksSub = this.footerSeoLinksService.getLinks(this.currentLanguage.code).subscribe((links) => this.seoLinks.set(links));
  }
}
